import { Box, Button, ButtonProps, LinkProps, Tooltip, TypographyOwnProps } from "@mui/material";
import { buttonColors, ReducedButtonColorTheme, SenecButtonLevel, SenecButtonSize } from "src/components/button/ButtonUtils";
import { SenecFPPTheme } from "src/styles/fpp-theme/theme.types";
import { styled } from "src/styles/fpp-theme/fppStyled";

export const buttonHeightSmall = "40px";
export const buttonHeightMedium = "48px";

export interface SenecButtonProps
    extends Omit<ButtonProps, "color" | "variant" | "size">,
        Omit<LinkProps, keyof TypographyOwnProps | keyof ButtonProps> {
    level?: SenecButtonLevel;
    size?: SenecButtonSize;
    colorTheme?: ReducedButtonColorTheme;
    tooltip?: string;
}

export const SenecButton = ({
    level = "primary",
    size = "medium",
    colorTheme = "light",
    tooltip,
    children,
    ...rest
}: SenecButtonProps) => {
    return (
        <Tooltip title={tooltip ?? undefined} placement="top" enterTouchDelay={0} leaveTouchDelay={1} enterNextDelay={100}>
            {/* wrapper is necessary to show tooltip on btn hover. Use Box because Stack contains invisible flex styles*/}
            <Box>
                <StyledButton level={level} size={size} colorTheme={colorTheme} {...rest}>
                    {children}
                </StyledButton>
            </Box>
        </Tooltip>
    );
};
interface StyledButtonProps extends ButtonProps, Omit<LinkProps, keyof TypographyOwnProps | keyof ButtonProps> {
    level: SenecButtonLevel;
    size: SenecButtonSize;
    colorTheme: ReducedButtonColorTheme;
}

const StyledButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== "colorTheme",
})<StyledButtonProps>(
    ({
        theme,
        level,
        size,
        colorTheme,
    }: {
        theme: SenecFPPTheme;
        level: SenecButtonLevel;
        size: SenecButtonSize;
        colorTheme: ReducedButtonColorTheme;
    }) => ({
        ...buttonStyles(theme, level, size, colorTheme),
    }),
);

export const buttonStyles = (
    theme: SenecFPPTheme,
    level: SenecButtonLevel,
    size: SenecButtonSize,
    colorTheme: ReducedButtonColorTheme,
) => {
    const palette = buttonColors(theme);

    return {
        borderRadius: "100px",
        border: "1px solid transparent",
        padding: "0.4rem 1rem",
        whiteSpace: "nowrap",
        "&.Mui-focusVisible": {
            outline: `${theme.palette.azur60.main} solid 2px`,
            outlineOffset: "2px",
        },
        ...(level === "primary" && {
            color: palette[colorTheme].primaryText,
            backgroundColor: palette[colorTheme].primaryMain,
            "&:hover": {
                color: theme.palette.white.main,
                backgroundColor: palette[colorTheme].primaryLight,
            },
            "&:active": {
                color: palette[colorTheme].primaryText,
                backgroundColor: palette[colorTheme].primaryDark,
            },
            "&.Mui-disabled": {
                color: palette[colorTheme].primaryText,
                backgroundColor: palette[colorTheme].primaryMain,
                opacity: 0.5,
            },
        }),
        ...(level === "secondary" && {
            padding: "calc(0.4rem - 1px) calc(1rem - 1px)",
            border: `1px solid ${palette[colorTheme].secondaryText}`,
            color: palette[colorTheme].secondaryText,
            "&:hover": {
                color: theme.palette.white.main,
                borderColor: palette[colorTheme].secondaryLight,
                backgroundColor: palette[colorTheme].secondaryLight,
            },
            "&:active": {
                color: palette[colorTheme].secondaryMain,
                borderColor: palette[colorTheme].secondaryDark,
                backgroundColor: palette[colorTheme].secondaryDark,
            },
            "&.Mui-disabled": {
                color: palette[colorTheme].secondaryText,
                borderColor: palette[colorTheme].secondaryText,
                opacity: 0.5,
            },
        }),
        ...(level === "tertiary" && {
            color: palette[colorTheme].tertiaryText,
            "&:hover": {
                color: palette[colorTheme].tertiaryLight,
                backgroundColor: "transparent",
            },
            "&:active": {
                color: palette[colorTheme].tertiaryDark,
            },
            "&.Mui-disabled": {
                color: palette[colorTheme].tertiaryText,
                opacity: 0.5,
            },
        }),
        ...(level === "warning" && {
            color: palette[colorTheme].warningText,
            backgroundColor: palette[colorTheme].warningMain,
            "&:hover": {
                color: theme.palette.white.main,
                backgroundColor: palette[colorTheme].warningLight,
            },
            "&:active": {
                color: palette[colorTheme].warningText,
                backgroundColor: palette[colorTheme].warningDark,
            },
            "&.Mui-disabled": {
                color: palette[colorTheme].warningText,
                backgroundColor: palette[colorTheme].warningMain,
                opacity: 0.5,
            },
            "&.Mui-focusVisible": {
                outline: `${theme.palette.warning.main} solid 2px`,
                outlineOffset: "2px",
            },
        }),
        ...(size === "small" && {
            ...theme.typography.buttonSmall,
            height: buttonHeightSmall,
            padding: "0.25rem 1.25rem",
            "& .MuiButton-startIcon > .MuiSvgIcon-root": {
                fontSize: "16px",
            },
            "& .MuiButton-endIcon > .MuiSvgIcon-root": {
                fontSize: "16px",
            },
        }),
        ...(size === "medium" && {
            height: buttonHeightMedium,
            padding: "0.5rem 1.75rem",
            "& .MuiButton-startIcon > .MuiSvgIcon-root": {
                fontSize: "24px",
            },
            "& .MuiButton-endIcon > .MuiSvgIcon-root": {
                fontSize: "24px",
            },
        }),
        ...(level === "secondary" &&
            size === "small" && {
                padding: "calc(0.25rem - 1px) calc(1.25rem - 1px)",
            }),
        ...(level === "secondary" &&
            size === "medium" && {
                padding: "calc(0.5rem - 1px) calc(1.75rem - 1px)",
            }),
    };
};
